import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import GlobalArea from "../components/elements/GlobalArea";
import store from "../redux/store";
import "../public/assets/css/main.css";
import "react-toastify/dist/ReactToastify.css";
import "react-responsive-modal/styles.css";
// Swiper Slider
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "swiper/css/grid";
import StorageWrapper from "../components/ecommerce/storage-wrapper";
import getReferrer from "../common/util/getReferrer";
import getUserAgent from "../common/util/getUserAgent";
import sendUserAgent from "../api/sendUserAgent";
import Error from "../components/layout/Error";
import { useRouter } from "next/router";
import { getDate, getTimeStamp } from "../common/util/getDate";
import { insertVisit } from "../api/visits";
import Script from "next/script";
import * as gtag from "../lib/gtag";
import { firebaseApp, getFCMToken } from "../lib/firebase";
import { io } from "socket.io-client";
import resources from "../api/constants/resources";
import { getCookie } from "../common/auth";
import {
  QueryClient,
  QueryClientProvider,
  Hydrate,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import useRouteTracker from "@hook/app/useRouteTracker";
import { useSiteConfig } from "@hook/app/useSiteConfig";
import useUserInfo from "@hook/app/useUserInfo";
import useBannerList from "@hook/app/useBannerList";
import { useDeviceDetect } from "@hook/app/useDeviceDetect";

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const [socket, setSocket] = useState(null);
  //siteConfig 데이터 세팅
  const { siteConfig, checkSiteSetting } = useSiteConfig();
  //사용자 라우팅 정보를 추적
  const { checkRouter } = useRouteTracker();
  // 유저정보 불러오는 함수
  const { userInfo, setUserInfo, getUserInfo } = useUserInfo();
  //배너정보 불러오는 함수
  const { getBannerList } = useBannerList();

  let referrer;
  let userAgent = globalThis.navigator?.userAgent;
  const userDevice = useDeviceDetect(userAgent);

  const userAgentHandler = async () => {
    const { origin, pathname, search, hash } = window.location;
    const decodedPath = decodeURIComponent(pathname); // 디코딩
    const currentUrl = `${origin}${decodedPath}${search}${hash}`;

    referrer = getReferrer();
    userAgent = getUserAgent();
    const body = {
      modes: "Connect",
      division: "store",
      referer: referrer,
      url: currentUrl,
      agent: userAgent,
    };
    const response = await sendUserAgent(body);

    if (response === "Success") return;
  };

  useEffect(() => {
    if (!window.location.hostname.includes("192.168")) {
      getFCMToken();
    }
    userAgentHandler();
    checkRouter(router.asPath);
    getUserInfo();
    getBannerList();
    const handleBeforeUnload = async (event) => {
      referrer = getReferrer();
      const today = getDate();
      const routes = sessionStorage.getItem("route");
      const products = sessionStorage.getItem(today);
      const body = {
        modes: "InsertSet",
        routes: routes || JSON.stringify([]),
        items: products || JSON.stringify([]),
        referrer: referrer,
      };

      const res = insertVisit(body);
      // 컴포넌트가 언마운트되기 전에 데이터 삭제
      sessionStorage.removeItem("route");
      sessionStorage.removeItem(today);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!router.isReady) return;

    if (!router.asPath) return;

    if (!window.location.hostname.includes("192.168")) {
      const newSocket = io(resources.SUPPORT);
      setSocket(newSocket);
      return () => {
        newSocket.disconnect();
      };
    }
  }, [router.isReady]);

  // socket
  useEffect(() => {
    if (socket) {
      // => 연결 성공
      socket?.on("connect", () => {
        // => 입장
        socket?.emit("joinRoom", "", "STORE", router.asPath);

        console.log("successfully connected.");
      });

      // 연결 해제
      socket?.on("disconnect", () => {
        console.log("disconnected.");
      });
    }
  }, [socket]);

  useEffect(() => {
    if (socket) {
      const isUserInfoValid = Object.keys(userInfo).length > 0 ? true : false;

      // 유저 현재 location
      const arr = [
        {
          url: router.asPath,
          ukey: isUserInfoValid ? userInfo?.Member?.ukey : "",
        },
      ];

      socket?.emit("storeAction", arr);
    }
  }, [router.asPath, userInfo]);

  useEffect(() => {
    if (!router.isReady) return;
    checkSiteSetting();
    if (getCookie("token")) {
      getUserInfo();
    }

    const handleRouteChange = (url) => {
      gtag.pageview(url);
      gtag.gtmPageview(url);
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("routeChangeComplete", (url) => checkRouter(url));
    // router.events.on("routeChangeComplete", (url) => slogCheck(url));
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("routeChangeComplete", (url) => checkRouter(url));
      // router.events.off("routeChangeComplete", (url) => slogCheck(url));
    };
  }, [router.isReady, router.events]);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // 전역 기본 설정 (개별 쿼리에서 재정의 가능)
            staleTime: 1000 * 60 * 5, // 5분
            gcTime: 1000 * 60 * 30, // 30분
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient} contextSharing={true}>
      {/*<Hydrate state={pageProps.dehydratedState}>*/}
      <link
        rel="stylesheet"
        href="https://cdn.ckeditor.com/ckeditor5/42.0.1/ckeditor5.css"
      />
      {/* ENP 공통스크립트 */}
      <Script
        async
        strategy="beforeInteractive"
        src="https://cdn.onetag.co.kr/0/tcs.js?eid=urgskck2lvmqurgskck2lv"
      />

      {/* Google tag (gtag.js)  */}
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${gtag.GA_TRACKING_ID}`}
      />
      <Script
        id="gtag-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${gtag.GA_TRACKING_ID}', {
              page_path: window.location.pathname,
              debug_mode: true
            });
          `,
        }}
      />
      {/* <!-- Google Tag Manager --> */}
      <Script
        id="google-tag-manager"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtag.GA_TAGMANAGER_ID}');
        `,
        }}
      />
      <Script
        strategy="afterInteractive"
        src="https://www.clarity.ms/tag/r8xn3v3ry3"
      />
      <Script
        strategy="afterInteractive"
        src="https://karrot-pixel.business.daangn.com/karrot-pixel.js"
      />
      <Script
        id="karrot-pixel-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.karrotPixel.init('1776243089895513001');
            window.karrotPixel.track('ViewPage');
          `,
        }}
      />
      <Script
        id="clarity-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)}
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)
      })(window, document, "clarity", "script", "r8xn3v3ry3");
    `,
        }}
      />
      {/* naver wcs_do START */}
      <Script
        id="naver-wcslog"
        strategy="beforeInteractive"
        defer=""
        src="//wcs.naver.net/wcslog.js"
      />
      {/* naver wcs_do END */}
      {/* <!-- digitalcamp 공통 전환 태그 BEGIN --> */}
      <Script
        id="digitalcamp"
        type="text/javascript"
        strategy="beforeInteractive"
        src="https://conv.digitalcamp.co.kr/tk.php?1255"
      />
      {/* <!-- digitalcamp 공통 전환 태그 END -->				 */}

      {/* BODY SCRIPT */}
      {/* <!-- WIDERPLANET  SCRIPT START 2023.5.8 --> */}
      <div id="wp_tg_cts" style={{ display: "none" }}></div>
      <Script
        id="wp_tg"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          var UserAgent = navigator.userAgent;
          var widerplanet_device = (UserAgent.match(/iPhone|iPod|Android|Windows CE|BlackBerry|Symbian|Windows Phone|webOS|Opera Mini|Opera Mobi|POLARIS|IEMobile|lgtelecom|nokia|SonyEricsson/i) != null || UserAgent.match(/LG|SAMSUNG|Samsung/) != null) ? "mobile" : "web";
          var wptg_tagscript_vars = wptg_tagscript_vars || [];
          wptg_tagscript_vars.push(
          (function() {
            return {
              wp_hcuid:"",
              ti:"54644",
              ty:"Home",	
              device: widerplanet_device	
            };
          }));
        `,
        }}
      />
      <Script
        id="wp_astg"
        strategy="afterInteractive"
        async
        src="//cdn-aitg.widerplanet.com/js/wp_astg_4.0.js"
      />
      {/* <!-- // WIDERPLANET  SCRIPT END 2023.5.8 --> */}

      <Script
        id="service-worker"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: ` if (typeof window !== "undefined" &&"serviceWorker" in navigator)
        {window.addEventListener("load", () => {
          navigator.serviceWorker.register("/firebase-messaging-sw.js", {
            updateViaCache: "none",
          });
        })}
        `,
        }}
      />

      <link rel="stylesheet" href="/assets/css/scriptOverride.css" />
      {siteConfig.SiteOpen === "N" ? (
        <Error siteMsg={siteConfig.SiteMsg} />
      ) : (
        <Provider store={store}>
          <StorageWrapper />
          <GlobalArea />
          <Component
            {...pageProps}
            userDevice={userDevice}
            siteConfig={siteConfig}
          />
        </Provider>
      )}
      {/* 네이버 공통스크립트 WCS DO START */}
      {!router.pathname.includes("shop-cart") &&
        !router.pathname.includes("checkout/complete") &&
        !router.pathname.includes("customizing/complete") && (
          <Script
            id="naver-wcs-inflow"
            dangerouslySetInnerHTML={{
              __html: `
          if(!wcs_add) var wcs_add = {}; 
          wcs_add["wa"] = "4a45209655561c";
          if (!_nasa) var _nasa={};
          if (window.wcs) {
            wcs.inflow("caretstore.co.kr");
            wcs_do(_nasa);
          }
        `,
            }}
          />
        )}
      {/* 네이버 공통스크립트 WCS DO END */}
      <Script src="https://nsp.pay.naver.com/sdk/js/naverpay.min.js" />
      <ReactQueryDevtools initialIsOpen={false} />
      {/*</Hydrate>*/}
    </QueryClientProvider>
  );
}

export default MyApp;
